import React from 'react';
import {StyledParagraph, StyledTitle} from "../CustomAntdComponents/Typography";
import {
    CommentInputWrapper,
    ContentWrapper,
    HeadingWrapper,
    Option, OptionGroupWrapper,
    OptionsWrapper,
    ReactionItem,
    ReactionsWrapper,
    Wrapper
} from "./styles";
import NeutralFace from "../../../src/assets/images/reactions/neutral-face.svg";
import SmilingFace  from "../../../src/assets/images/reactions/smiling-face.svg";
import GrinningFace from "../../../src/assets/images/reactions/grinning-face-with-smiling-eyes.svg";
import {EXCELLENT_REACTION, FAIR_REACTION, GOOD_REACTION} from "../BookingFeedback/constant";
import StyledTextarea from "../CustomAntdComponents/StyledTextarea";

export interface FeedbackFormData {
    positiveFeedback: string[];
    improvementAreas: string[];
    comment: string;
}

type Props = {
    reaction: string;
    feedbackData?: FeedbackFormData;
    onChange?: (data: FeedbackFormData) => void;
}

const options = [
    {
        label: 'Prices were fair',
        value: 'Prices were fair'
    },
    {
        label: 'No hidden fees',
        value: 'No hidden fees'
    },
    {
        label: 'It was easy to book',
        value: 'It was easy to book'
    },
    {
        label: 'Fast loading time',
        value: 'Fast loading time'
    }
];

const improvementOptions = [
    {
        label: 'Loading time',
        value: 'Loading time'
    },
    {
        label: 'Available inventory',
        value: 'Available inventory'
    },
    {
        label: 'Difficult to book',
        value: 'Difficult to book'
    },
    {
        label: 'Unclear policy',
        value: 'Unclear policy'
    }
];

const FeedbackForm = ({ reaction, feedbackData, onChange }: Props) => {
    const [positiveFeedback, setPositiveFeedback] = React.useState<string[]>(
        feedbackData?.positiveFeedback || ['Prices were fair']
    );
    const [improvementAreas, setImprovementAreas] = React.useState<string[]>(
        feedbackData?.improvementAreas || ['Loading time']
    );
    const [comment, setComment] = React.useState<string>(
        feedbackData?.comment || ''
    );

    const togglePositive = (val: string) => {
        const next = positiveFeedback.includes(val)
            ? positiveFeedback.filter(item => item !== val)
            : [...positiveFeedback, val];
        setPositiveFeedback(next);
        onChange?.({ positiveFeedback: next, improvementAreas, comment });
    };

    const toggleImprovement = (val: string) => {
        const next = improvementAreas.includes(val)
            ? improvementAreas.filter(item => item !== val)
            : [...improvementAreas, val];
        setImprovementAreas(next);
        onChange?.({ positiveFeedback, improvementAreas: next, comment });
    };

    const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const nextComment = e.target.value;
        setComment(nextComment);
        onChange?.({ positiveFeedback, improvementAreas, comment: nextComment });
    };

    const renderReactionIcon = () => {
       switch (reaction) {
           case GOOD_REACTION:
               return <img src={SmilingFace} alt={reaction} />;
           case FAIR_REACTION:
               return <img src={NeutralFace} alt={reaction} />;
           case EXCELLENT_REACTION:
               return <img src={GrinningFace} alt={reaction} />;
           default:
               return <img src={NeutralFace} alt={reaction} />;
       }
    };

    return (
        <Wrapper>
            <HeadingWrapper>
                <StyledTitle level={4} align="center">
                    Your experience with Trips
                </StyledTitle>
                <ReactionsWrapper>
                    <ReactionItem>
                        {renderReactionIcon()}
                        <StyledParagraph size="normal">
                            {reaction}
                        </StyledParagraph>
                    </ReactionItem>
                </ReactionsWrapper>
            </HeadingWrapper>

            <ContentWrapper>
                <OptionGroupWrapper>
                    <StyledTitle level={4}>
                        What did Trips do right?
                    </StyledTitle>

                    <OptionsWrapper>
                        {
                            options.map((option, index) => (
                                <Option 
                                    active={positiveFeedback.includes(option.value)} 
                                    onClick={() => togglePositive(option.value)} 
                                    key={index}
                                >
                                    <StyledParagraph>
                                        {option.label}
                                    </StyledParagraph>
                                </Option>
                            ))
                        }
                    </OptionsWrapper>
                </OptionGroupWrapper>
                <OptionGroupWrapper>
                    <StyledTitle level={4}>
                        What could Trips improve on?
                    </StyledTitle>

                    <OptionsWrapper>
                        {
                            improvementOptions.map((option, index) => (
                                <Option 
                                    active={improvementAreas.includes(option.value)} 
                                    onClick={() => toggleImprovement(option.value)} 
                                    key={index}
                                >
                                    <StyledParagraph>
                                        {option.label}
                                    </StyledParagraph>
                                </Option>
                            ))
                        }
                    </OptionsWrapper>
                </OptionGroupWrapper>

                <CommentInputWrapper>
                    <StyledTitle level={4}>
                        Anything else to add? (optional)
                    </StyledTitle>

                    <StyledTextarea 
                        rows={4} 
                        placeholder="Enter your feedback here"
                        value={comment}
                        onChange={handleCommentChange}
                    />

                    <StyledParagraph size="normal">
                        Your feedback means the world to us. It helps us to improve how we serve you.
                    </StyledParagraph>
                </CommentInputWrapper>
            </ContentWrapper>
        </Wrapper>
    );
};

export default FeedbackForm;

